"use client";

import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ThemeText } from "@/components/ui/theme-utils";

interface Stat {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
  decimals?: number;
}

const STATS: Stat[] = [
  { value: 100, suffix: "m+", label: "Files delivered" },
  { value: 1.0, suffix: " Gbps", decimals: 1, label: "Sustained throughput" },
  { value: 150, suffix: "TB", label: "of Assets under management" },
  { value: 14, label: "Global edge PoPs" }
];

export function Stats() {
  return (
    <section className="py-12 sm:py-16">
      <div className="container mx-auto px-5 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-3xl bg-gradient-to-br from-brand-500/30 via-brand-700/30 to-brand-900/30 p-px shadow-elevated">
          {STATS.map((s, i) => (
            <StatCard key={i} stat={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ stat }: { stat: Stat }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1400;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setN(eased * stat.value);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, stat.value]);

  return (
    <div
      ref={ref}
      className="bg-background px-6 sm:px-8 py-7 sm:py-9 flex flex-col"
    >
      <div className="flex items-baseline gap-0.5">
        {stat.prefix && (
          <span className="text-2xl sm:text-3xl font-bold text-foreground/80">
            {stat.prefix}
          </span>
        )}
        <motion.span
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gradient"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
        >
          {stat.decimals ? n.toFixed(stat.decimals) : Math.round(n)}
        </motion.span>
        {stat.suffix && (
          <span className="text-2xl sm:text-3xl font-bold text-foreground/80">
            {stat.suffix}
          </span>
        )}
      </div>
      <ThemeText variant="muted" className="text-xs sm:text-sm mt-1">
        {stat.label}
      </ThemeText>
    </div>
  );
}
